import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore.js';

const ROUTES = {
  client: '/client', mechanic: '/mechanic', electrician: '/mechanic',
  painter: '/mechanic', reception: '/reception', billing: '/billing', admin: '/admin',
};

function Brand({ sub }) {
  return (
    <div className="auth-brand">
      <img src="/logo.png" alt="Autonation" className="auth-logo" onError={e => { e.target.style.display = 'none'; }} />
      <div className="auth-brand-name">AUTONATION</div>
      <div className="auth-brand-sub">Taller Automotriz</div>
      <div className="auth-brand-divider" />
      {sub && (
        <div style={{ marginTop: '0.65rem', fontSize: '0.75rem', color: 'var(--silver-dark)', letterSpacing: '0.05em' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Divider({ text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '1rem 0' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border, rgba(255,255,255,0.08))' }} />
      <span style={{ color: 'var(--silver-dark)', fontSize: '0.78rem' }}>{text}</span>
      <div style={{ flex: 1, height: 1, background: 'var(--border, rgba(255,255,255,0.08))' }} />
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder = '••••••••', required, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="form-input"
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        autoFocus={autoFocus}
        style={{ paddingRight: '2.8rem' }}
      />
      <button
        type="button"
        onClick={() => setShow(v => !v)}
        tabIndex={-1}
        aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        style={{
          position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem',
          color: 'var(--silver-dark)', lineHeight: 1, fontSize: '1.1rem',
        }}
      >
        {show ? (
          // Eye-off icon
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        ) : (
          // Eye icon
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        )}
      </button>
    </div>
  );
}

export { Brand, Divider };

export default function Login() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('modo') === 'cliente' ? 'client' : 'staff';
  const [mode, setMode]         = useState(initialMode);
  const [phone, setPhone]       = useState('+593');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const { login, clientLogin } = useAuthStore();
  const navigate = useNavigate();

  async function handleClientLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await clientLogin(phone, password);
      navigate(ROUTES[user.role] || '/client');
    } catch (err) {
      setError(err.response?.data?.error || 'Teléfono o contraseña incorrectos');
    } finally { setLoading(false); }
  }

  async function handleStaffLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await login(email, password);
      navigate(ROUTES[user.role] || '/');
    } catch {
      setError('Credenciales incorrectas');
    } finally { setLoading(false); }
  }

  if (mode === 'client') {
    return (
      <div className="auth-layout">
        <form onSubmit={handleClientLogin} className="auth-card">
          <Brand sub="Acceso clientes" />

          <div className="form-group">
            <label className="form-label">Número de teléfono</label>
            <input className="form-input" type="tel" placeholder="+593 99 999 9999"
              value={phone} onChange={e => setPhone(e.target.value)} required autoFocus />
          </div>

          <div className="form-group">
            <label className="form-label">Contraseña</label>
            <PasswordInput value={password} onChange={e => setPassword(e.target.value)} required />
          </div>

          {error && <div className="error-text">{error}</div>}

          <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: '1rem' }}>
            {loading ? 'Ingresando…' : 'Ingresar →'}
          </button>

          <div className="hint-text" style={{ marginTop: '0.8rem', textAlign: 'center' }}>
            <Link to="/forgot-password" className="link">¿Olvidaste tu contraseña?</Link>
          </div>

          <div className="hint-text" style={{ marginTop: '1rem' }}>
            ¿Eres cliente nuevo? <Link to="/register" className="link">Crear cuenta</Link>
          </div>

          <button type="button" onClick={() => navigate('/')}
            style={{ marginTop: '0.5rem', color: 'var(--silver-dark)', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '0.83rem' }}>
            ← Volver
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <form onSubmit={handleStaffLogin} className="auth-card">
        <Brand sub="Panel de acceso — Taller" />

        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" placeholder="tu@autonation.com.ec"
            value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
        </div>

        <div className="form-group">
          <label className="form-label">Contraseña</label>
          <PasswordInput value={password} onChange={e => setPassword(e.target.value)} required />
        </div>

        {error && <div className="error-text">{error}</div>}

        <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: '1rem' }}>
          {loading ? 'Ingresando…' : 'Ingresar →'}
        </button>

        <button type="button" onClick={() => navigate('/')}
          style={{ marginTop: '0.8rem', color: 'var(--silver-dark)', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '0.83rem' }}>
          ← Volver
        </button>
      </form>
    </div>
  );
}
